/**
 * Contratos e rótulos do painel inicial.
 *
 * ⚠️ `ROTULOS` traduz a chave crua do banco (`win10`) para a legenda. A chave
 * é o que vai para o filtro ao clicar numa fatia — nunca o rótulo, que muda.
 */
/* ── Contratos ───────────────────────────────────────────────────────────── */

export type FormaCartao = 'numero' | 'barras' | 'rosca' | 'tabela';

export interface Cartao {
  readonly id: string;
  /** Indicador pronto (chamados, violações…). Quando presente, ignora a consulta. */
  readonly indicador?: string | null;
  readonly titulo: string;
  readonly forma: FormaCartao;
  readonly largura: 1 | 2 | 3;
  readonly limiteAlerta: number | null;
  readonly nota: string | null;
  /**
   * A consulta estruturada inteira — não só a explicação.
   *
   * O tipo antes trazia apenas `explicacao`, o que bastava para desenhar o
   * cartão mas não para **editá-lo** nem para abrir o Inventário com o mesmo
   * filtro. Guardar menos do que se tem obriga a adivinhar depois.
   */
  readonly consulta: {
    readonly explicacao: string;
    readonly intencao?: string;
    readonly filtros?: readonly unknown[];
    readonly agruparPor?: string | null;
  };
}

export interface Variacao {
  readonly delta: number;
  readonly dias: number;
  readonly valorAnterior: number;
}

export interface DadosCartao {
  readonly cartaoId: string;
  readonly total: number;
  readonly grupos: readonly { readonly chave: string; readonly total: number }[];
  readonly itens: readonly Record<string, unknown>[];
  /** Variação desde a última medição comparável. `null` = ainda não dá para dizer. */
  readonly variacao: Variacao | null;
  readonly serie: readonly { readonly valor: number; readonly em: string }[];
  readonly projecao: {
    readonly diasMin: number;
    readonly diasMax: number;
    readonly baseDias: number;
    readonly texto: string;
  } | null;
  readonly erro: string | null;
}

export interface IndicadorDisponivel {
  readonly chave: string;
  readonly titulo: string;
  readonly nota: string;
  readonly leva: string;
  readonly exige: string;
}

export interface Pendencia {
  readonly chave: string;
  readonly titulo: string;
  readonly total: number;
  readonly gravidade: 'critico' | 'atencao' | 'informativo';
  readonly destino: string;
  readonly detalhe: string;
}

export interface RespostaPendencias {
  readonly pendencias: readonly Pendencia[];
  readonly sync: {
    readonly em: string | null;
    readonly horasAtras: number | null;
    readonly obsoleto: boolean;
    readonly automatico?: boolean;
    readonly intervaloMin: number;
    readonly erro: string | null;
  };
}

export interface RespostaInicial {
  readonly painel: {
    readonly id: string | null;
    readonly nome: string;
    readonly descricao: string;
    readonly cartoes: readonly Cartao[];
  };
  readonly deFabrica: boolean;
  readonly dados: readonly DadosCartao[];
}

/**
 * Paleta dos gráficos.
 *
 * Sequência fixa e ordenada, não aleatória: a mesma categoria recebe a mesma
 * cor toda vez que o painel abre. Cor que muda entre visitas obriga a reler a
 * legenda a cada abertura, e o gráfico deixa de ser reconhecível.
 *
 * O vermelho da marca fica **fora** desta lista de propósito — ele significa
 * "ação" e "alerta" no resto do produto, e uma fatia vermelha só porque é a
 * terceira da lista viraria alarme falso.
 */
export const CORES = ['#5b8def', '#3fb98f', '#f0a94c', '#9b7ede', '#4bb7d1', '#b08968', '#8a9bb0'];

/**
 * Rótulos legíveis para as chaves de agrupamento.
 *
 * O banco guarda `win7_ou_anterior`, `macos`, `unix` — nomes de coluna, não
 * nomes para gente. A tabela do inventário já era traduzida; o gráfico não era,
 * e ficou com a legenda falando em código enquanto a tabela ao lado falava em
 * português.
 */
export const ROTULOS: Record<string, string> = {
  win11: 'Windows 11',
  win10: 'Windows 10',
  win8: 'Windows 8',
  win7_ou_anterior: 'Windows 7 ou anterior',
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  unix: 'Unix',
  android: 'Android',
  outro: 'Outro',
  '(não informado)': 'Não informado',
};

export const rotular = (chave: string): string => ROTULOS[chave] ?? chave;

/** Maior primeiro: lê-se de cima para baixo, e o que importa vem antes. */
export const ordenar = (
  g: readonly { readonly chave: string; readonly total: number }[],
): readonly { readonly chave: string; readonly total: number }[] =>
  [...g].sort((a, b) => b.total - a.total);
